/**
 * Sign In Modal Component
 *
 * Modal for user authentication with Supabase.
 * Supports email/password sign in and forgot password flow.
 */

import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { resetPassword } from '../lib/supabase';

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SignInModal({ isOpen, onClose }: SignInModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState<'sign-in' | 'forgot-password' | 'reset-sent'>('sign-in');
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const { signIn, isConfigured } = useAuth();
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Focus email input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset view when modal closes
  useEffect(() => {
    if (!isOpen) {
      setView('sign-in');
      setResetError(null);
      setResetEmail('');
    }
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setIsSubmitting(true);

    try {
      await signIn(email, password);
      onClose();
      navigate('/console');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      // Provide friendlier error messages
      if (message.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please try again.');
      } else if (message.includes('Email not confirmed')) {
        setError('Please check your email and confirm your account.');
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setResetError(null);

    if (!resetEmail.trim()) {
      setResetError('Email is required');
      return;
    }

    setIsResetting(true);
    try {
      await resetPassword(resetEmail);
      setView('reset-sent');
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsResetting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-md mx-4 bg-white rounded-3xl shadow-2xl border border-slate-200 p-8 animate-[fadeInUp_0.3s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors border-0 bg-transparent"
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Saqr Skills
          </span>
        </div>

        {/* Sign-in view */}
        {view === 'sign-in' && (
          <>
            {/* Title */}
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              Welcome back
            </h2>
            <p className="text-sm text-slate-500 mb-8">
              Sign in to your account to continue.
            </p>

            {/* Supabase not configured warning */}
            {!isConfigured && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm mb-6">
                <p className="font-medium">Authentication not configured</p>
                <p className="text-xs mt-1 text-amber-600">
                  Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.
                </p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>
                <input
                  ref={emailInputRef}
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/20 outline-none transition-all"
                  placeholder="you@company.com"
                  autoComplete="email"
                  disabled={!isConfigured || isSubmitting}
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/20 outline-none transition-all"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={!isConfigured || isSubmitting}
                />
              </div>

              {/* Forgot password link */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => { setView('forgot-password'); setError(null); }}
                  className="text-sm text-sky-500 hover:text-sky-600 bg-transparent border-0 cursor-pointer p-0"
                >
                  Forgot password?
                </button>
              </div>

              {/* Error message */}
              {error && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={!isConfigured || isSubmitting}
                className="w-full h-12 rounded-full bg-sky-500 px-8 text-sm font-semibold text-white hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/30 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Help text */}
            <div className="mt-6 text-center">
              <p className="text-xs text-slate-400">
                Contact your administrator if you need access.
              </p>
            </div>
          </>
        )}

        {/* Forgot password view */}
        {view === 'forgot-password' && (
          <>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Reset password</h2>
            <p className="text-sm text-slate-500 mb-8">
              Enter your email and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="reset-email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/20 outline-none transition-all"
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                  disabled={isResetting}
                />
              </div>
              {resetError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  {resetError}
                </div>
              )}
              <button
                type="submit"
                disabled={isResetting}
                className="w-full h-12 rounded-full bg-sky-500 px-8 text-sm font-semibold text-white hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/30 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResetting ? 'Sending...' : 'Send Reset Link'}
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setView('sign-in'); setResetError(null); }}
                  className="text-sm text-slate-500 hover:text-slate-700 bg-transparent border-0 cursor-pointer"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          </>
        )}

        {/* Reset sent confirmation view */}
        {view === 'reset-sent' && (
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-6">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-500">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Check your email</h2>
            <p className="text-sm text-slate-500 mb-8">
              We've sent a password reset link to <strong>{resetEmail}</strong>.
              Check your inbox and follow the link to reset your password.
            </p>
            <button
              type="button"
              onClick={() => { setView('sign-in'); setResetEmail(''); }}
              className="text-sm text-sky-500 hover:text-sky-600 bg-transparent border-0 cursor-pointer font-medium"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
